import { Grid } from "@mui/material";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import AcxToggleButton from "components/UI/AcxToggleButton";
import AcxDrawer from "components/UI/Drawer/AcxDrawer";
import { observer } from "mobx-react";
import React from "react";
import { EnumToDropdownOptionConversion } from "utils/EnumToDropdownOptionConversion";
import { useStore } from "utils/useStore";
import OrganizationWorkflowsCreateConditionForm from "./Components/OrganizationWorkflowsCreateConditionForm";
import OrganizationWorkflowsEmailEditor from "./Components/OrganizationWorkflowsEmailEditor";
import OrganizationWorkflowsActionBar from "./OrganizationWorkflowsActionBar";
import OrganizationWorkflowsListConditions from "./OrganizationWorkflowsListConditions";
import OrganizationWorkflowsListEmails from "./OrganizationWorkflowsListEmails";
import OrganizationWorkflowEmailStore from "./Stores/OrganizationWorkflowEmailStore";
import OrganizationWorkflowsStore, {
    LOAD_CONDITIONS,
    LOAD_CONTACT_TYPES,
} from "./Stores/OrganizationWorkflowsStore";
import AcxSelectMulti from "components/UI/Select/BaseSelectComponents/AcxSelectMulti";
import { AuthStore } from "stores/AuthStore";

export enum OrganizationWorkflowViews {
    "Workflow Conditions" = 0,
    "Email Templates" = 1,
}

type Props = {
    orgId: string;
};

const OrganizationWorkflowsWrapper: React.FC<Props> = observer(({ orgId }) => {
    const conditionStore = useStore(OrganizationWorkflowsStore);
    const emailStore = useStore(OrganizationWorkflowEmailStore);
    const authStore = useStore(AuthStore);
    const drawerStore = conditionStore.drawerStore;

    React.useEffect(() => {
        if (orgId) {
            conditionStore.setOrganizationId(orgId);
            emailStore.setOrganizationId(orgId);
        }
    }, [orgId, conditionStore, emailStore]);

    return (
        <>
            <AcxDrawer
                disableBackdrop={false}
                anchor={drawerStore.anchor}
                open={drawerStore.isOpen}
                collapsible={drawerStore.collapseSize}
                drawerStore={drawerStore}
                customRootStyles={drawerStore.customRootStyles}
                onDrawerClose={conditionStore.handleDrawerClose}
                onDrawerOpen={conditionStore.handleDrawerOpen}
                offsetPosition={drawerStore.offsetPosition}
                offsetPx={drawerStore.offsetPx}
                content={
                    <OrganizationWorkflowsCreateConditionForm
                        store={conditionStore}
                    />
                }
            />

            {conditionStore.getTaskLoading(LOAD_CONDITIONS) ? (
                <AcxLoadingIndicator
                    color="secondary"
                    alternate="PuffLoader"
                    size={175}
                />
            ) : (
                <Grid
                    container
                    sx={{
                        width: "85%",
                        marginLeft: "auto",
                        marginRight: "auto",
                        marginY: 4.5,
                    }}
                >
                    {emailStore.emailEditorIsOpen ? (
                        <OrganizationWorkflowsEmailEditor
                            emailStore={emailStore}
                        />
                    ) : (
                        <>
                            <Grid
                                item
                                xs={12}
                                style={{ marginBottom: "0.5rem" }}
                            >
                                <AcxToggleButton
                                    id="organization-workflows-view-selector"
                                    exclusive
                                    onChange={conditionStore.handleViewChange}
                                    spacing={3}
                                    items={EnumToDropdownOptionConversion(
                                        OrganizationWorkflowViews,
                                    ).map((view) => {
                                        return {
                                            value: view.value,
                                            element: view.label,
                                        };
                                    })}
                                    defaultValue={conditionStore.selectedView}
                                    inputLabel="Select Your View..."
                                    darkBg
                                    style={{ display: "inline-block" }}
                                    labelSx={{ fontSize: "13px" }}
                                />
                            </Grid>

                            <Grid
                                container
                                item
                                xs={12}
                                style={{ marginBottom: "0.5rem" }}
                            >
                                <OrganizationWorkflowsActionBar
                                    conditionStore={conditionStore}
                                    emailStore={emailStore}
                                />
                            </Grid>

                            {authStore.canUserEdit(
                                "Additional Overdue Recipients",
                            ) && (
                                <Grid container item xs={4}>
                                    <AcxSelectMulti
                                        required
                                        infoText="These contacts will be notified via email when Workflows are overdue, due today, or due tomorrow within their assigned hierarchies. They will continue to receive notice daily until overdue Workflows are resolved."
                                        inputLabel="Additional Overdue Recipients"
                                        menuPortalTarget={document.body}
                                        defaultValue={
                                            conditionStore.additionalOverdueRecipients
                                        }
                                        id="additionalOverdueRecipients"
                                        labelField="name"
                                        valueField="id"
                                        options={
                                            conditionStore.availableContactTypes ??
                                            []
                                        }
                                        onChange={(e) => {
                                            conditionStore.handleAdditionalOverdueRecipients(
                                                e,
                                            );
                                        }}
                                        isDisabled={conditionStore.getTaskLoading(
                                            LOAD_CONTACT_TYPES,
                                        )}
                                        placeholder={
                                            conditionStore.getTaskLoading(
                                                LOAD_CONTACT_TYPES,
                                            )
                                                ? "Loading"
                                                : "Select a Hierarchy Contact Type..."
                                        }
                                        containerHeight="auto"
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                {conditionStore.selectedView ===
                                OrganizationWorkflowViews[
                                    "Workflow Conditions"
                                ] ? (
                                    <OrganizationWorkflowsListConditions
                                        store={conditionStore}
                                    />
                                ) : (
                                    <OrganizationWorkflowsListEmails
                                        store={emailStore}
                                    />
                                )}
                            </Grid>
                        </>
                    )}
                </Grid>
            )}
        </>
    );
});

export default OrganizationWorkflowsWrapper;
